import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Mail, Phone, RefreshCw, Search, Loader2, Share2, Flame, Target, Layers, Copy, ExternalLink, Linkedin, Facebook, MessageCircle, Zap } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { adminHeaders, clearAdminToken } from "@/lib/adminAuth";

const TYPES = ["all", "sponsor", "franchise", "player", "academy", "media", "general"];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Routes that have dynamic broadcast OG cards via /api/og/<key>/og-image.png
const OG_ROUTES = [
  { key: "home",         path: "/",              label: "Home" },
  { key: "about",        path: "/about",         label: "About" },
  { key: "teams",        path: "/teams",         label: "Teams" },
  { key: "schedule",     path: "/schedule",      label: "Schedule" },
  { key: "standings",    path: "/standings",     label: "Standings" },
  { key: "players",      path: "/players",       label: "Players" },
  { key: "news",         path: "/news",          label: "News" },
  { key: "sponsorships", path: "/sponsorships",  label: "Sponsorships" },
  { key: "partnerships", path: "/partnerships",  label: "Partnerships" },
  { key: "academies",    path: "/academies",     label: "Academies" },
  { key: "media",        path: "/media",         label: "Media" },
  { key: "contact",      path: "/contact",       label: "Contact" },
];

const TYPE_COLORS = {
  sponsor:   "bg-pshl-electric/15 text-pshl-electric border-pshl-electric/40",
  franchise: "bg-pshl-gold/15 text-pshl-gold border-pshl-gold/40",
  player:    "bg-pshl-red/15 text-pshl-red border-pshl-red/40",
  academy:   "bg-emerald-400/15 text-emerald-400 border-emerald-400/40",
  media:     "bg-sky-400/15 text-sky-400 border-sky-400/40",
  general:   "bg-white/10 text-white border-white/20",
};

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [shareStats, setShareStats] = useState(null);
  const [ctaFunnel, setCtaFunnel] = useState(null);
  const [ctaByPage, setCtaByPage] = useState(null);
  const [ctaConversions, setCtaConversions] = useState(null);
  const [showConversions, setShowConversions] = useState(false);
  const [showBrandLockup, setShowBrandLockup] = useState(false);
  const [lockupTab, setLockupTab] = useState("routes");  // routes | teams | news
  const [teams, setTeams] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { interest_type: filter };
      const [leadsRes, statsRes, shareRes, ctaRes, pageRes, convRes, teamsRes, newsRes] = await Promise.all([
        api.get("/admin/leads", { params, headers: adminHeaders() }),
        api.get("/admin/stats", { headers: adminHeaders() }),
        api.get("/admin/shares/stats", { params: { since_days: 30 }, headers: adminHeaders() }),
        api.get("/admin/cta-funnel", { params: { since_days: 30 }, headers: adminHeaders() }),
        api.get("/admin/cta-by-page", { params: { since_days: 30, min_impressions: 1 }, headers: adminHeaders() }),
        api.get("/admin/cta-conversions", { params: { since_days: 30 }, headers: adminHeaders() }),
        api.get("/teams"),
        api.get("/news"),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
      setShareStats(shareRes.data);
      setCtaFunnel(ctaRes.data);
      setCtaByPage(pageRes.data);
      setCtaConversions(convRes.data);
      setTeams(teamsRes.data || []);
      setNews(newsRes.data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired — please sign in again.");
        clearAdminToken();
        navigate("/admin/login", { replace: true });
        return;
      }
      toast.error("Failed to load leads.");
    } finally {
      setLoading(false);
    }
  }, [filter, navigate]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter((l) =>
      [l.name, l.email, l.phone, l.organization, l.city, l.message]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [leads, search]);

  const logout = () => {
    clearAdminToken();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div data-testid="admin-leads-page" className="pt-8 pb-16">
      <div className="pshl-container">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.32em] text-pshl-electric font-display mb-2">PSHL8 · Admin Console</div>
            <h1 className="font-display text-3xl md:text-4xl text-white tracking-tighter">Lead Inbox</h1>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="admin-refresh" onClick={load} className="inline-flex items-center gap-2 border border-white/15 hover:border-white text-white font-display uppercase tracking-[0.18em] text-xs px-4 py-2.5">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 border border-white/15 hover:border-pshl-red hover:text-pshl-red text-white font-display uppercase tracking-[0.18em] text-xs px-4 py-2.5">
              <LogOut className="h-3.5 w-3.5" /> Sign Out
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div data-testid="admin-stats" className="grid grid-cols-2 md:grid-cols-7 gap-2 mb-8">
            <div className="bg-pshl-surface border border-white/10 p-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/55">Total</div>
              <div className="font-display text-3xl text-white">{stats.total}</div>
            </div>
            {Object.entries(stats.by_type).map(([k, v]) => (
              <div key={k} className="bg-pshl-surface border border-white/10 p-4">
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/55">{k}</div>
                <div className="font-display text-2xl text-white">{v}</div>
              </div>
            ))}
          </div>
        )}

        {/* CTA A/B Funnel */}
        {ctaFunnel && ctaFunnel.variants.length > 0 && (
          <div data-testid="admin-cta-funnel" className="bg-pshl-surface border border-white/10 p-6 mb-8">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 grid place-items-center bg-pshl-gold/15 border border-pshl-gold/40">
                  <Share2 className="h-4 w-4 text-pshl-gold" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-pshl-gold font-display">Sticky CTA · A/B Funnel</div>
                  <div className="font-display text-xl text-white">Last {ctaFunnel.since_days} days</div>
                </div>
              </div>
              {ctaConversions && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowConversions((v) => !v)}
                    data-testid="admin-cta-conversions-toggle"
                    className="inline-flex items-center gap-2 border border-pshl-gold/40 hover:border-pshl-gold text-pshl-gold hover:bg-pshl-gold/10 font-display uppercase tracking-[0.18em] text-[11px] px-4 py-2 transition-colors"
                  >
                    <Target className="h-3.5 w-3.5" />
                    {showConversions ? "Hide" : "Track"} Conversions
                    <span className="text-white/55 normal-case tracking-normal ml-1">↗</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowBrandLockup((v) => !v)}
                    data-testid="admin-brand-lockup-toggle"
                    className="inline-flex items-center gap-2 border border-pshl-electric/50 hover:border-pshl-electric text-pshl-electric hover:bg-pshl-electric/10 font-display uppercase tracking-[0.18em] text-[11px] px-4 py-2 transition-colors"
                  >
                    <Layers className="h-3.5 w-3.5" />
                    {showBrandLockup ? "Hide" : "Brand"} Lockups
                  </button>
                </div>
              )}
            </div>
            <div className="grid sm:grid-cols-3 gap-3 border-t border-white/10 pt-5">
              {ctaFunnel.variants.map((v) => (
                <div key={v.variant} data-testid={`admin-cta-variant-${v.variant}`} className="border border-white/10 bg-white/[0.02] p-4 hover:border-pshl-gold transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-display uppercase tracking-[0.2em] border border-pshl-gold/40 bg-pshl-gold/10 text-pshl-gold">
                      Variant {v.variant.toUpperCase()}
                    </span>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-white/55">CTR</div>
                      <div className="font-display text-2xl text-pshl-gold">{v.ctr_pct}%</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 pt-3 border-t border-white/8">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-white/55">Impr.</div>
                      <div className="font-display text-lg text-white">{v.impressions}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-white/55">Clicks</div>
                      <div className="font-display text-lg text-pshl-electric">{v.clicks}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sponsor Conversions — joins cta-click → lead by session_id */}
        {showConversions && ctaConversions && (
          <div data-testid="admin-cta-conversions" className="bg-pshl-surface border border-pshl-gold/30 p-6 mb-8">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 grid place-items-center bg-pshl-gold/15 border border-pshl-gold/40">
                  <Target className="h-4 w-4 text-pshl-gold" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-pshl-gold font-display">Sponsor Conversions</div>
                  <div className="font-display text-xl text-white">CTA click → lead · Last {ctaConversions.since_days} days</div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-5 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/55">Sessions Clicked</div>
                  <div data-testid="conv-sessions-clicked" className="font-display text-2xl text-white">{ctaConversions.sessions_clicked}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/55">Converted</div>
                  <div data-testid="conv-sessions-converted" className="font-display text-2xl text-pshl-electric">{ctaConversions.sessions_converted}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/55">Conv. Rate</div>
                  <div data-testid="conv-rate" className="font-display text-2xl text-pshl-gold">{ctaConversions.conversion_pct}%</div>
                </div>
              </div>
            </div>

            {ctaConversions.sessions_clicked === 0 ? (
              <div className="text-white/55 text-sm py-4 flex items-center gap-2 border-t border-white/10 pt-5">
                <Target className="h-4 w-4" /> No sessions have clicked the CTA in this window yet. Conversions appear once a visitor clicks and then submits a lead form on the same browser.
              </div>
            ) : (
              <div className="border-t border-white/10 pt-5 grid lg:grid-cols-2 gap-6">
                {/* By variant */}
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/55 mb-3 font-display">Conversion by Variant</div>
                  <div className="space-y-2">
                    {ctaConversions.by_variant.length === 0 ? (
                      <div className="text-white/45 text-sm">—</div>
                    ) : ctaConversions.by_variant.map((v) => (
                      <div key={v.variant} data-testid={`conv-variant-${v.variant}`} className="flex items-center justify-between border border-white/8 px-4 py-2.5 bg-white/[0.02]">
                        <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-display uppercase tracking-[0.2em] border border-pshl-gold/40 bg-pshl-gold/10 text-pshl-gold">
                          Variant {String(v.variant).toUpperCase()}
                        </span>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="font-mono text-xs text-white/70">{v.sessions_converted}/{v.sessions_clicked}</span>
                          <span className="font-display text-pshl-gold text-lg w-16 text-right">{v.conversion_pct}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* By interest type */}
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/55 mb-3 font-display">Conversions by Interest</div>
                  <div className="space-y-2">
                    {Object.keys(ctaConversions.by_interest_type).length === 0 ? (
                      <div className="text-white/45 text-sm">—</div>
                    ) : Object.entries(ctaConversions.by_interest_type).map(([k, count]) => (
                      <div key={k} data-testid={`conv-interest-${k}`} className="flex items-center justify-between border border-white/8 px-4 py-2.5 bg-white/[0.02]">
                        <span className={`inline-block text-[10px] font-display uppercase tracking-[0.2em] px-2 py-0.5 border ${TYPE_COLORS[k] || TYPE_COLORS.general}`}>
                          {k}
                        </span>
                        <span className="font-display text-white text-lg">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {ctaConversions.recent_conversions && ctaConversions.recent_conversions.length > 0 && (
              <div className="border-t border-white/10 pt-5 mt-6">
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/55 mb-3 font-display">Recent Attributed Leads</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-white/55">
                        <th className="text-left font-display text-[11px] uppercase tracking-[0.2em] py-2 pr-3">Lead</th>
                        <th className="text-left font-display text-[11px] uppercase tracking-[0.2em] py-2 px-3">Interest</th>
                        <th className="text-left font-display text-[11px] uppercase tracking-[0.2em] py-2 px-3">Click Page</th>
                        <th className="text-left font-display text-[11px] uppercase tracking-[0.2em] py-2 px-3">Variant</th>
                        <th className="text-right font-display text-[11px] uppercase tracking-[0.2em] py-2 pl-3">Converted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ctaConversions.recent_conversions.map((r, idx) => (
                        <tr key={`${r.email || "noemail"}-${r.lead_created_at || idx}`} data-testid={`conv-recent-${idx}`} className="hover:bg-white/5 border-t border-white/5">
                          <td className="py-2 pr-3">
                            <div className="text-white">{r.name}</div>
                            <div className="text-xs text-white/55">{r.organization || r.email}</div>
                          </td>
                          <td className="py-2 px-3">
                            <span className={`inline-block text-[10px] font-display uppercase tracking-[0.2em] px-2 py-0.5 border ${TYPE_COLORS[r.interest_type] || TYPE_COLORS.general}`}>
                              {r.interest_type}
                            </span>
                          </td>
                          <td className="py-2 px-3 font-mono text-xs text-white/80">{r.click_page || "/"}</td>
                          <td className="py-2 px-3 font-display text-pshl-gold uppercase text-xs">{r.click_variant}</td>
                          <td className="py-2 pl-3 text-right font-mono text-xs text-white/65 whitespace-nowrap">
                            {new Date(r.lead_created_at).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Live Brand Lockup — preview every OG share card + copy share URLs */}
        {showBrandLockup && (() => {
          const tabsData = {
            routes: OG_ROUTES.map((r) => ({
              key: r.key, label: r.label, sublabel: r.path,
              ogUrl: `${BACKEND_URL}/api/og/${r.key}/og-image.png`,
              shareUrl: `${BACKEND_URL}/api/share/page/${r.key}`,
              purgeId: r.key,
            })),
            teams: teams.map((t) => ({
              key: t.slug, label: t.name, sublabel: `/teams/${t.slug}`,
              ogUrl: `${BACKEND_URL}/api/teams/${t.slug}/og-image.png`,
              shareUrl: `${BACKEND_URL}/api/share/team/${t.slug}`,
              purgeId: t.slug,
            })),
            news: news.map((n) => ({
              key: n.id, label: n.title, sublabel: `/news/${n.id}`,
              ogUrl: `${BACKEND_URL}/api/news/${n.id}/og-image.png`,
              shareUrl: `${BACKEND_URL}/api/share/news/${n.id}`,
              purgeId: n.id,
            })),
          };
          const currentItems = tabsData[lockupTab] || [];
          const tabPills = [
            { id: "routes", label: "Routes", count: tabsData.routes.length },
            { id: "teams",  label: "Teams",  count: tabsData.teams.length },
            { id: "news",   label: "News",   count: tabsData.news.length },
          ];
          return (
          <div data-testid="admin-brand-lockup" className="bg-pshl-surface border border-pshl-electric/30 p-6 mb-8">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 grid place-items-center bg-pshl-electric/15 border border-pshl-electric/40">
                  <Layers className="h-4 w-4 text-pshl-electric" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-pshl-electric font-display">Live Brand Lockup · OG Preview Grid</div>
                  <div className="font-display text-xl text-white">
                    {currentItems.length} broadcast share card{currentItems.length === 1 ? "" : "s"}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      // Purge only the cached files relevant to the current tab
                      await Promise.all(currentItems.map((it) =>
                        api.post(`/admin/og/${it.purgeId}/purge`, null, { headers: adminHeaders() })
                      ));
                      // Cache-bust every preview
                      document.querySelectorAll('[data-og-preview]').forEach((img) => {
                        const base = img.getAttribute('data-og-base');
                        if (base) img.setAttribute('src', `${base}?t=${Date.now()}`);
                      });
                      toast.success(`Re-scraped ${currentItems.length} ${lockupTab} card${currentItems.length === 1 ? "" : "s"}`, {
                        description: "Local previews refreshed. Open individual tiles' debuggers to nudge X/LinkedIn/FB.",
                      });
                    } catch (e) {
                      toast.error("Bulk purge failed", { description: e?.message });
                    }
                  }}
                  data-testid="admin-brand-lockup-rescrape-all"
                  className="inline-flex items-center gap-2 border border-pshl-gold/50 hover:border-pshl-gold text-pshl-gold hover:bg-pshl-gold/10 font-display uppercase tracking-[0.18em] text-[11px] px-4 py-2 transition-colors"
                >
                  <Zap className="h-3.5 w-3.5" /> Re-scrape All {currentItems.length}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    document.querySelectorAll('[data-og-preview]').forEach((img) => {
                      const base = img.getAttribute('data-og-base');
                      if (base) img.setAttribute('src', `${base}?t=${Date.now()}`);
                    });
                    toast.success("Reloaded all 12 OG previews");
                  }}
                  data-testid="admin-brand-lockup-refresh"
                  className="inline-flex items-center gap-2 border border-white/15 hover:border-white text-white font-display uppercase tracking-[0.18em] text-[11px] px-4 py-2 transition-colors"
                >
                  <RefreshCw className="h-3.5 w-3.5" /> Refresh
                </button>
              </div>
            </div>
            {/* Sub-tab pills */}
            <div data-testid="admin-brand-lockup-tabs" className="flex items-center gap-2 mb-4 flex-wrap">
              {tabPills.map((p) => {
                const active = lockupTab === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setLockupTab(p.id)}
                    data-testid={`admin-brand-lockup-tab-${p.id}`}
                    className={`inline-flex items-center gap-2 px-4 py-2 border font-display uppercase tracking-[0.18em] text-[11px] transition-colors ${
                      active
                        ? "bg-pshl-electric/15 border-pshl-electric text-pshl-electric"
                        : "border-white/10 text-white/65 hover:border-white/30 hover:text-white"
                    }`}
                  >
                    {p.label}
                    <span className={`font-mono text-[10px] px-1.5 py-0.5 ${active ? "bg-pshl-electric/25 text-white" : "bg-white/5 text-white/55"}`}>
                      {p.count}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 border-t border-white/10 pt-5">
              {currentItems.length === 0 ? (
                <div className="col-span-full text-white/55 text-sm py-6 text-center">No items in this tab yet.</div>
              ) : currentItems.map((r) => {
                const ogBase = r.ogUrl;
                const shareUrl = r.shareUrl;
                const enc = encodeURIComponent(shareUrl);
                const shareText = encodeURIComponent(`PSHL∞ · ${r.label} — Power · Speed · Hustle · Legacy`);
                const debuggers = [
                  { id: "x",        title: "Test on X (draft tweet preview)",        url: `https://twitter.com/intent/tweet?url=${enc}&text=${shareText}`,        Icon: null,   letter: "𝕏" },
                  { id: "linkedin", title: "LinkedIn Post Inspector",                 url: `https://www.linkedin.com/post-inspector/inspect/${enc}`,                Icon: Linkedin },
                  { id: "facebook", title: "Facebook Sharing Debugger (also WhatsApp)", url: `https://developers.facebook.com/tools/debug/?q=${enc}`,              Icon: Facebook },
                  { id: "whatsapp", title: "Open WhatsApp with this link",            url: `https://api.whatsapp.com/send?text=${encodeURIComponent(`${r.label} — ${shareUrl}`)}`, Icon: MessageCircle },
                ];
                const copy = async () => {
                  try {
                    await navigator.clipboard.writeText(shareUrl);
                    toast.success(`Copied ${r.label} share URL`);
                  } catch {
                    toast.error("Couldn't copy");
                  }
                };
                const forceRescrape = async () => {
                  try {
                    // 1) Bust our own OG cache so the next platform fetch gets fresh bytes
                    await api.post(`/admin/og/${r.purgeId}/purge`, null, { headers: adminHeaders() });
                    // 2) Bust the local <img> preview too
                    const img = document.querySelector(`[data-testid="brand-lockup-tile-${r.key}"] img[data-og-preview]`);
                    if (img) img.setAttribute("src", `${ogBase}?t=${Date.now()}`);
                    // 3) Open FB + LinkedIn debuggers — they will fetch the now-fresh card
                    window.open(`https://developers.facebook.com/tools/debug/?q=${enc}`, "_blank", "noopener");
                    window.open(`https://www.linkedin.com/post-inspector/inspect/${enc}`, "_blank", "noopener");
                    toast.success(`Re-scrape kicked off for ${r.label}`, {
                      description: "Cache purged + FB/LinkedIn debuggers opened in new tabs.",
                    });
                  } catch (e) {
                    toast.error("Couldn't purge OG cache", { description: e?.message });
                  }
                };
                return (
                  <div
                    key={r.key}
                    data-testid={`brand-lockup-tile-${r.key}`}
                    className="border border-white/10 hover:border-pshl-electric transition-colors bg-black/40 group"
                  >
                    <div className="aspect-[1200/630] bg-[#020A1A] overflow-hidden">
                      <img
                        src={ogBase}
                        data-og-preview
                        data-og-base={ogBase}
                        alt={`${r.label} OG preview`}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-3 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-display text-sm text-white truncate uppercase tracking-[0.14em]" title={r.label}>{r.label}</div>
                        <div className="font-mono text-[10px] text-white/55 truncate">{r.sublabel}</div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={copy}
                          data-testid={`brand-lockup-copy-${r.key}`}
                          title="Copy share URL"
                          className="p-1.5 border border-white/15 hover:border-pshl-gold hover:bg-pshl-gold/10 hover:text-pshl-gold text-white/75 transition-colors"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                        <a
                          href={shareUrl}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`brand-lockup-open-${r.key}`}
                          title="Open share URL (crawler preview)"
                          className="p-1.5 border border-white/15 hover:border-pshl-electric hover:bg-pshl-electric/10 hover:text-pshl-electric text-white/75 transition-colors"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </div>
                    </div>
                    {/* Per-platform unfurl debuggers + Force Re-scrape */}
                    <div className="px-3 pb-3 flex items-center gap-1 border-t border-white/5 pt-2 flex-wrap">
                      <span className="text-[9px] uppercase tracking-[0.22em] text-white/45 font-display mr-1">Test:</span>
                      {debuggers.map((d) => (
                        <a
                          key={d.id}
                          href={d.url}
                          target="_blank"
                          rel="noreferrer"
                          title={d.title}
                          data-testid={`brand-lockup-${d.id}-${r.key}`}
                          className="p-1.5 border border-white/10 hover:border-pshl-electric hover:bg-pshl-electric/10 hover:text-pshl-electric text-white/65 transition-colors inline-flex items-center justify-center min-w-[28px]"
                        >
                          {d.Icon ? <d.Icon className="h-3.5 w-3.5" /> : <span className="text-[13px] leading-none font-display">{d.letter}</span>}
                        </a>
                      ))}
                      <button
                        type="button"
                        onClick={forceRescrape}
                        title="Force Re-scrape: purge our OG cache + open FB & LinkedIn debuggers"
                        data-testid={`brand-lockup-rescrape-${r.key}`}
                        className="ml-auto p-1.5 border border-pshl-gold/40 hover:border-pshl-gold hover:bg-pshl-gold/10 text-pshl-gold transition-colors inline-flex items-center gap-1.5"
                      >
                        <Zap className="h-3.5 w-3.5" />
                        <span className="text-[10px] font-display uppercase tracking-[0.18em]">Re-scrape</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 text-[11px] text-white/55 leading-relaxed">
              Each tile previews the live 1200×630 broadcast OG card served at <span className="font-mono">/api/og/&lt;key&gt;/og-image.png</span>, <span className="font-mono">/api/teams/&lt;slug&gt;/og-image.png</span> or <span className="font-mono">/api/news/&lt;id&gt;/og-image.png</span>. Use the
              copy button to grab a crawler-friendly share URL — paste anywhere on WhatsApp/X/LinkedIn and it&apos;ll unfurl with the broadcast card.
            </div>
          </div>
          );
        })()}

        {/* Sponsor-Warm Pages */}
        {ctaByPage && ctaByPage.pages.length > 0 && (
          <div data-testid="admin-cta-by-page" className="bg-pshl-surface border border-white/10 p-6 mb-8">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 grid place-items-center bg-pshl-electric/15 border border-pshl-electric/40">
                  <Flame className="h-4 w-4 text-pshl-electric" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-pshl-electric font-display">Sponsor-Warm Pages</div>
                  <div className="font-display text-xl text-white">CTR by page · Last {ctaByPage.since_days} days</div>
                </div>
              </div>
            </div>
            <div className="border-t border-white/10 pt-5 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-white/55">
                    <th className="text-left font-display text-xs uppercase tracking-[0.2em] py-2 pr-3">Page</th>
                    <th className="text-right font-display text-xs uppercase tracking-[0.2em] py-2 px-3">Impr.</th>
                    <th className="text-right font-display text-xs uppercase tracking-[0.2em] py-2 px-3">Clicks</th>
                    <th className="text-right font-display text-xs uppercase tracking-[0.2em] py-2 pl-3">CTR</th>
                  </tr>
                </thead>
                <tbody>
                  {ctaByPage.pages.map((p) => (
                    <tr key={p.page} data-testid={`admin-cta-page-${p.page.replace(/\//g, "_") || "root"}`} className="hover:bg-white/5">
                      <td className="py-2 pr-3 font-mono text-xs text-white/85 truncate max-w-[420px]">{p.page || "/"}</td>
                      <td className="py-2 px-3 text-right font-mono text-white/80">{p.impressions}</td>
                      <td className="py-2 px-3 text-right font-mono text-pshl-electric">{p.clicks}</td>
                      <td className="py-2 pl-3 text-right font-display text-pshl-electric">{p.ctr_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Share Velocity */}
        {shareStats && (
          <div data-testid="admin-share-velocity" className="bg-pshl-surface border border-white/10 p-6 mb-8">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 grid place-items-center bg-pshl-red/15 border border-pshl-red/40">
                  <Flame className="h-4 w-4 text-pshl-red" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-pshl-red font-display">Share Velocity</div>
                  <div className="font-display text-xl text-white">Last {shareStats.since_days} days</div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/55">All-time</div>
                  <div className="font-display text-2xl text-white">{shareStats.total_all_time}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/55">In window</div>
                  <div className="font-display text-2xl text-pshl-electric">{shareStats.total_in_window}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/55">Team</div>
                  <div className="font-display text-2xl text-white">{shareStats.by_kind.team}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/55">News</div>
                  <div className="font-display text-2xl text-white">{shareStats.by_kind.news}</div>
                </div>
              </div>
            </div>

            {shareStats.top.length === 0 ? (
              <div className="text-white/55 text-sm py-4 flex items-center gap-2 border-t border-white/10 pt-5">
                <Share2 className="h-4 w-4" /> No shares yet in this window.
              </div>
            ) : (
              <div className="border-t border-white/10 pt-5 grid md:grid-cols-2 gap-3">
                {shareStats.top.map((row, i) => (
                  <div key={`${row.kind}-${row.target_id}`} data-testid={`admin-top-share-${row.target_id}`} className="flex items-center gap-3 border border-white/8 px-4 py-3 hover:border-pshl-red transition-colors">
                    <div className="font-mono text-xs text-pshl-red w-7">#{String(i + 1).padStart(2, "0")}</div>
                    <span className={`text-[10px] uppercase tracking-[0.22em] font-display px-2 py-0.5 border ${row.kind === "team" ? "text-pshl-electric border-pshl-electric/40 bg-pshl-electric/10" : "text-pshl-gold border-pshl-gold/40 bg-pshl-gold/10"}`}>
                      {row.kind}
                    </span>
                    <div className="flex-1 text-sm text-white truncate">{row.label}</div>
                    <div className="font-display text-pshl-red text-lg">{row.count}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-5">
          {TYPES.map((t) => (
            <button
              key={t}
              data-testid={`admin-filter-${t}`}
              onClick={() => setFilter(t)}
              className={`text-[11px] font-display uppercase tracking-[0.22em] px-4 py-2 border transition-colors ${
                filter === t ? "bg-pshl-electric border-pshl-electric text-white" : "border-white/15 text-white/80 hover:border-white"
              }`}
            >
              {t}
            </button>
          ))}
          <div className="ml-auto relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/45" />
            <input
              data-testid="admin-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, org…"
              className="pshl-input pl-9"
            />
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="py-20 flex items-center justify-center text-white/60">
            <Loader2 className="h-5 w-5 animate-spin mr-3" /> Loading leads…
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center text-white/55 border border-white/10">No leads match this filter.</div>
        ) : (
          <div data-testid="admin-leads-table" className="overflow-x-auto border border-white/10">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-white/55">
                  <th className="text-left font-display text-xs uppercase tracking-[0.2em] py-4 px-4 border-b border-white/10">Submitted</th>
                  <th className="text-left font-display text-xs uppercase tracking-[0.2em] py-4 px-4 border-b border-white/10">Name / Org</th>
                  <th className="text-left font-display text-xs uppercase tracking-[0.2em] py-4 px-4 border-b border-white/10">Contact</th>
                  <th className="text-left font-display text-xs uppercase tracking-[0.2em] py-4 px-4 border-b border-white/10 hidden md:table-cell">Interest</th>
                  <th className="text-left font-display text-xs uppercase tracking-[0.2em] py-4 px-4 border-b border-white/10 hidden lg:table-cell">City</th>
                  <th className="text-left font-display text-xs uppercase tracking-[0.2em] py-4 px-4 border-b border-white/10 hidden lg:table-cell">Message</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr key={l.id} data-testid={`admin-lead-row-${l.id}`} className="hover:bg-white/5 transition-colors align-top">
                    <td className="py-4 px-4 border-b border-white/5 font-mono text-xs text-white/70 whitespace-nowrap">
                      {new Date(l.created_at).toLocaleString()}
                    </td>
                    <td className="py-4 px-4 border-b border-white/5">
                      <div className="font-display text-white text-base">{l.name}</div>
                      <div className="text-xs text-white/55">{l.organization || "—"}</div>
                    </td>
                    <td className="py-4 px-4 border-b border-white/5">
                      <a href={`mailto:${l.email}`} className="flex items-center gap-1.5 text-sm text-white hover:text-pshl-electric">
                        <Mail className="h-3.5 w-3.5" /> {l.email}
                      </a>
                      <a href={`tel:${l.phone}`} className="flex items-center gap-1.5 text-xs text-white/65 hover:text-pshl-electric mt-1">
                        <Phone className="h-3 w-3" /> {l.phone}
                      </a>
                    </td>
                    <td className="py-4 px-4 border-b border-white/5 hidden md:table-cell">
                      <span className={`inline-block text-[10px] font-display uppercase tracking-[0.2em] px-2 py-1 border ${TYPE_COLORS[l.interest_type] || TYPE_COLORS.general}`}>
                        {l.interest_type}
                      </span>
                    </td>
                    <td className="py-4 px-4 border-b border-white/5 hidden lg:table-cell text-white/80">{l.city || "—"}</td>
                    <td className="py-4 px-4 border-b border-white/5 hidden lg:table-cell text-white/75 max-w-md">{l.message || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
